<template>
  <div class="fd">
    <!-- 顶栏：关卡/计时/计数/提示 -->
    <div class="fd-bar">
      <span class="fd-info">关卡 {{ level }} / 30</span>
      <span class="fd-info">⏱ {{ seconds }} 秒</span>
      <span class="fd-info">🔍 {{ foundCount }} / {{ diffTotal }}</span>
      <span class="fd-info" :class="{ warn: misses > 0 }">👎 {{ misses }}</span>
      <button
          type="button"
          class="fd-hint-btn"
          :disabled="hints <= 0 || won"
          title="提示：标记一处差异位置"
          @click="useHint">
        💡 提示 ×{{ hints }}
      </button>
      <button type="button" class="fd-refresh" title="重新开始本关" @click="loadLevel(level)">🔄</button>
    </div>

    <!-- 选关 -->
    <div class="fd-levels" :class="{ open: levelOpen }">
      <button
          v-for="lv in 30"
          :key="lv"
          class="fd-level"
          :class="{
            active: lv === level,
            locked: lv > unlockedLevel
          }"
          @click="pickLevel(lv)">
        {{ lv }}
      </button>
    </div>
    <button type="button" class="fd-menu-toggle" @click="levelOpen = !levelOpen">
      {{ levelOpen ? '收起选关 ▲' : '选择关卡 ▼' }}
    </button>

    <!-- 双画布 -->
    <div class="fd-canvas-wrap">
      <canvas
          ref="canvasA"
          class="fd-canvas"
          width="720"
          height="580"
          @click="onCanvasClick($event, 'A')"></canvas>
      <div class="fd-vs">找不同</div>
      <canvas
          ref="canvasB"
          class="fd-canvas"
          width="720"
          height="580"
          @click="onCanvasClick($event, 'B')"></canvas>
    </div>

    <!-- 过关遮罩 -->
    <div v-if="won" class="fd-overlay">
      <div class="fd-overlay-emoji">🎉</div>
      <p class="fd-overlay-title">全部找齐啦！</p>
      <p class="fd-overlay-info">
        第 {{ level }} 关 · 用时 {{ seconds }} 秒 · 错点 {{ misses }} 次
      </p>
      <div class="fd-overlay-btns">
        <button v-if="level < 30" type="button" class="fd-btn fd-btn-pink" @click="loadLevel(level + 1)">下一关</button>
        <button type="button" class="fd-btn fd-btn-line" @click="loadLevel(level)">重玩本关</button>
      </div>
    </div>
  </div>
</template>

<script>
/* =====================================================
 * 粉星找不同（复刻 y7x7 大家来找茬）
 * 双画布程序生成可爱场景，右侧有 5 处差异
 * 30 关 / 提示次数（每日登录 +1）/ 选关 / 计时
 * ===================================================== */
const W = 720
const H = 580
const DIFF_TOTAL = 5
const MAX_LEVEL = 30

const PALETTE = ['#ff8fc7', '#ff6fa5', '#e25b96', '#c9a6ff', '#9b6dff', '#ffd98a', '#7ec8ff', '#4fc6a4', '#f26ab6', '#ffab9e']
const TYPES = ['star', 'heart', 'flower', 'cloud', 'moon', 'butterfly', 'balloon', 'gem', 'candy', 'bow']
const MUTATIONS = ['color', 'size', 'type', 'gone']

const LV_KEY = 'pink_finddiff_level'
const HINT_KEY = 'pink_finddiff_hints'
const HINT_DAY_KEY = 'pink_finddiff_hint_day'

export default {
  name: 'FindDiff',
  data () {
    return {
      level: 1,
      unlockedLevel: 1,
      seconds: 0,
      timer: null,
      found: [],
      misses: 0,
      hints: 1,
      won: false,
      levelOpen: false,
      elements: [],
      diffs: [], // 差异元素在 elements 中的下标
      missMarkers: []
    }
  },
  computed: {
    diffTotal () {
      return DIFF_TOTAL
    },
    foundCount () {
      return this.found.length
    }
  },
  created () {
    // 每日登录提示奖励 +1
    try {
      const today = new Date()
      const dayStr = today.getFullYear() + '-' + (today.getMonth() + 1) + '-' + today.getDate()
      const lastDay = localStorage.getItem(HINT_DAY_KEY)
      let hints = parseInt(localStorage.getItem(HINT_KEY), 10) || 1
      if (lastDay !== dayStr) {
        hints = Math.min(3, hints + 1)
        localStorage.setItem(HINT_DAY_KEY, dayStr)
        localStorage.setItem(HINT_KEY, String(hints))
      }
      this.hints = Math.max(1, hints)
      const savedLevel = parseInt(localStorage.getItem(LV_KEY), 10) || 1
      this.unlockedLevel = Math.min(MAX_LEVEL, savedLevel)
    } catch (e) {
      this.hints = 1
    }
  },
  beforeDestroy () {
    this.stopTimer()
  },
  methods: {
    /* ---------- 关卡 ---------- */
    loadLevel (lv) {
      if (lv < 1 || lv > MAX_LEVEL) return
      this.level = lv
      this.won = false
      this.found = []
      this.misses = 0
      this.missMarkers = []
      this.seconds = 0
      this.stopTimer()
      this.buildScene(lv)
      this.$nextTick(() => this.drawAll())
      this.timer = setInterval(() => {
        if (!this.won) this.seconds++
      }, 1000)
    },
    pickLevel (lv) {
      if (lv > this.unlockedLevel) {
        this.flashLocked()
        return
      }
      this.loadLevel(lv)
    },
    flashLocked () {
      // 简单提示：暂时把提示文案变红闪一下
      this.levelOpen = true
    },

    /* ---------- 场景生成 ---------- */
    buildScene (lv) {
      // 元素数量随关卡增加
      const count = Math.min(56, 26 + lv)
      const cols = 6
      const rows = Math.ceil(count / cols)
      const cellW = W / cols
      const cellH = H / rows
      const rnd = this.rand
      const elements = []
      for (let i = 0; i < count; i++) {
        const cx = (i % cols) * cellW + cellW / 2
        const cy = Math.floor(i / cols) * cellH + cellH / 2
        const jx = (rnd() * 2 - 1) * cellW * 0.22
        const jy = (rnd() * 2 - 1) * cellH * 0.22
        const type = TYPES[Math.floor(rnd() * TYPES.length)]
        elements.push({
          idx: i,
          type,
          x: Math.max(26, Math.min(W - 26, cx + jx)),
          y: Math.max(26, Math.min(H - 26, cy + jy)),
          size: 16 + rnd() * 14,
          color: PALETTE[Math.floor(rnd() * PALETTE.length)],
          rot: rnd() * Math.PI * 2
        })
      }
      // 挑选差异
      const pool = elements.slice()
      const diffs = []
      while (diffs.length < DIFF_TOTAL && pool.length) {
        const k = Math.floor(rnd() * pool.length)
        const el = pool.splice(k, 1)[0]
        diffs.push(el.idx)
        el.mutation = MUTATIONS[Math.floor(rnd() * MUTATIONS.length)]
        if (el.mutation === 'color') {
          // 换个明显不同的颜色
          let c
          do {
            c = PALETTE[Math.floor(rnd() * PALETTE.length)]
          } while (c === el.color)
          el.mutColor = c
        } else if (el.mutation === 'size') {
          el.mutSize = el.size * (0.5 + rnd() * 0.15)
        } else if (el.mutation === 'type') {
          let t
          do {
            t = TYPES[Math.floor(rnd() * TYPES.length)]
          } while (t === el.type)
          el.mutType = t
        }
      }
      this.elements = elements
      this.diffs = diffs
    },
    rand () {
      return Math.random()
    },

    /* ---------- 绘制 ---------- */
    drawAll () {
      const ca = this.$refs.canvasA
      const cb = this.$refs.canvasB
      if (!ca || !cb) return
      const ctxA = ca.getContext('2d')
      const ctxB = cb.getContext('2d')
      this.drawScene(ctxA, false)
      this.drawScene(ctxB, true)
      this.drawMissMarkers(ctxA)
      this.drawMissMarkers(ctxB)
    },
    drawScene (ctx, diffed) {
      ctx.clearRect(0, 0, W, H)
      // 背景渐变
      const bg = ctx.createLinearGradient(0, 0, 0, H)
      bg.addColorStop(0, '#ffeef6')
      bg.addColorStop(0.5, '#ffe3f0')
      bg.addColorStop(1, '#f3e6ff')
      ctx.fillStyle = bg
      ctx.fillRect(0, 0, W, H)
      // 背景小星星（两侧一致）
      ctx.save()
      for (let i = 0; i < 26; i++) {
        const sx = (i * 97 + 33) % W
        const sy = (i * 61 + 17) % H
        const sr = 1.2 + (i % 3)
        ctx.globalAlpha = 0.35 + (i % 4) * 0.15
        ctx.fillStyle = i % 2 ? '#ffd6ec' : '#d9c2ff'
        ctx.beginPath()
        ctx.arc(sx, sy, sr, 0, Math.PI * 2)
        ctx.fill()
      }
      ctx.restore()
      // 元素
      for (const el of this.elements) {
        let type = el.type
        let color = el.color
        let size = el.size
        if (diffed && el.mutation) {
          if (el.mutation === 'gone') continue
          if (el.mutation === 'color') color = el.mutColor
          if (el.mutation === 'size') size = el.mutSize
          if (el.mutation === 'type') type = el.mutType
        }
        this.drawElement(ctx, type, el.x, el.y, size, color, el.rot)
      }
      // 已找到的差异圈（两侧都画）
      for (const idx of this.found) {
        const el = this.elements[idx]
        ctx.save()
        ctx.strokeStyle = '#ff3d8f'
        ctx.lineWidth = 4
        ctx.setLineDash([10, 8])
        ctx.beginPath()
        ctx.arc(el.x, el.y, Math.max(24, el.size * 0.9), 0, Math.PI * 2)
        ctx.stroke()
        ctx.restore()
      }
    },
    drawElement (ctx, type, x, y, size, color, rot) {
      ctx.save()
      ctx.translate(x, y)
      ctx.rotate(rot)
      ctx.fillStyle = color
      ctx.strokeStyle = 'rgba(255,255,255,0.85)'
      ctx.lineWidth = Math.max(2, size * 0.12)
      const s = size
      ctx.beginPath()
      if (type === 'star') {
        for (let i = 0; i < 10; i++) {
          const r = i % 2 === 0 ? s : s * 0.45
          const a = -Math.PI / 2 + i * Math.PI / 5
          const px = Math.cos(a) * r
          const py = Math.sin(a) * r
          if (i === 0) ctx.moveTo(px, py)
          else ctx.lineTo(px, py)
        }
        ctx.closePath()
        ctx.fill()
        ctx.stroke()
      } else if (type === 'heart') {
        ctx.moveTo(0, s * 0.4)
        ctx.bezierCurveTo(-s * 1.1, -s * 0.5, -s * 0.5, -s * 1.1, 0, -s * 0.35)
        ctx.bezierCurveTo(s * 0.5, -s * 1.1, s * 1.1, -s * 0.5, 0, s * 0.4)
        ctx.closePath()
        ctx.fill()
        ctx.stroke()
      } else if (type === 'flower') {
        for (let i = 0; i < 5; i++) {
          const a = i * Math.PI * 2 / 5
          ctx.beginPath()
          ctx.arc(Math.cos(a) * s * 0.55, Math.sin(a) * s * 0.55, s * 0.42, 0, Math.PI * 2)
          ctx.fill()
          ctx.stroke()
        }
        ctx.beginPath()
        ctx.arc(0, 0, s * 0.34, 0, Math.PI * 2)
        ctx.fillStyle = '#ffe9a8'
        ctx.fill()
        ctx.stroke()
      } else if (type === 'cloud') {
        ctx.beginPath()
        ctx.arc(-s * 0.55, 0, s * 0.42, 0, Math.PI * 2)
        ctx.arc(0, -s * 0.25, s * 0.5, 0, Math.PI * 2)
        ctx.arc(s * 0.55, 0, s * 0.42, 0, Math.PI * 2)
        ctx.arc(0, s * 0.2, s * 0.48, 0, Math.PI * 2)
        ctx.fill()
        ctx.stroke()
      } else if (type === 'moon') {
        ctx.arc(0, 0, s * 0.8, 0, Math.PI * 2)
        ctx.fill()
        ctx.save()
        ctx.globalCompositeOperation = 'destination-out'
        ctx.beginPath()
        ctx.arc(s * 0.42, -s * 0.28, s * 0.7, 0, Math.PI * 2)
        ctx.fill()
        ctx.restore()
        ctx.stroke()
      } else if (type === 'butterfly') {
        ctx.beginPath()
        ctx.ellipse(-s * 0.5, -s * 0.35, s * 0.5, s * 0.65, -0.4, 0, Math.PI * 2)
        ctx.fill()
        ctx.stroke()
        ctx.beginPath()
        ctx.ellipse(s * 0.5, -s * 0.35, s * 0.5, s * 0.65, 0.4, 0, Math.PI * 2)
        ctx.fill()
        ctx.stroke()
        ctx.beginPath()
        ctx.ellipse(-s * 0.45, s * 0.45, s * 0.35, s * 0.45, 0.3, 0, Math.PI * 2)
        ctx.fill()
        ctx.stroke()
        ctx.beginPath()
        ctx.ellipse(s * 0.45, s * 0.45, s * 0.35, s * 0.45, -0.3, 0, Math.PI * 2)
        ctx.fill()
        ctx.stroke()
        ctx.fillStyle = '#6b4a5a'
        ctx.beginPath()
        ctx.ellipse(0, 0, s * 0.14, s * 0.9, 0, 0, Math.PI * 2)
        ctx.fill()
      } else if (type === 'balloon') {
        ctx.beginPath()
        ctx.ellipse(0, -s * 0.3, s * 0.62, s * 0.75, 0, 0, Math.PI * 2)
        ctx.fill()
        ctx.stroke()
        ctx.beginPath()
        ctx.moveTo(0, s * 0.35)
        ctx.lineTo(s * 0.35, s * 0.6)
        ctx.lineTo(-s * 0.35, s * 0.6)
        ctx.closePath()
        ctx.fill()
        ctx.stroke()
        ctx.strokeStyle = 'rgba(180,130,160,0.7)'
        ctx.lineWidth = 2
        ctx.beginPath()
        ctx.moveTo(0, s * 0.6)
        ctx.quadraticCurveTo(s * 0.2, s * 1.1, -s * 0.1, s * 1.4)
        ctx.stroke()
      } else if (type === 'gem') {
        ctx.moveTo(0, -s * 0.9)
        ctx.lineTo(s * 0.7, -s * 0.2)
        ctx.lineTo(s * 0.45, s * 0.75)
        ctx.lineTo(-s * 0.45, s * 0.75)
        ctx.lineTo(-s * 0.7, -s * 0.2)
        ctx.closePath()
        ctx.fill()
        ctx.stroke()
        ctx.beginPath()
        ctx.moveTo(-s * 0.7, -s * 0.2)
        ctx.lineTo(0, s * 0.15)
        ctx.lineTo(s * 0.7, -s * 0.2)
        ctx.strokeStyle = 'rgba(255,255,255,0.75)'
        ctx.stroke()
      } else if (type === 'candy') {
        ctx.beginPath()
        ctx.ellipse(0, 0, s * 0.72, s * 0.45, 0, 0, Math.PI * 2)
        ctx.fill()
        ctx.stroke()
        ctx.fillStyle = 'rgba(255,255,255,0.7)'
        ctx.beginPath()
        ctx.ellipse(-s * 0.2, -s * 0.12, s * 0.18, s * 0.12, -0.4, 0, Math.PI * 2)
        ctx.fill()
        ctx.beginPath()
        ctx.moveTo(-s * 0.72, 0)
        ctx.lineTo(-s * 1.25, -s * 0.4)
        ctx.lineTo(-s * 1.25, s * 0.4)
        ctx.closePath()
        ctx.fill()
        ctx.stroke()
        ctx.beginPath()
        ctx.moveTo(s * 0.72, 0)
        ctx.lineTo(s * 1.25, -s * 0.4)
        ctx.lineTo(s * 1.25, s * 0.4)
        ctx.closePath()
        ctx.fill()
        ctx.stroke()
      } else if (type === 'bow') {
        ctx.beginPath()
        ctx.moveTo(0, 0)
        ctx.lineTo(-s * 1.0, -s * 0.6)
        ctx.lineTo(-s * 0.55, s * 0.25)
        ctx.closePath()
        ctx.fill()
        ctx.stroke()
        ctx.beginPath()
        ctx.moveTo(0, 0)
        ctx.lineTo(s * 1.0, -s * 0.6)
        ctx.lineTo(s * 0.55, s * 0.25)
        ctx.closePath()
        ctx.fill()
        ctx.stroke()
        ctx.beginPath()
        ctx.arc(0, 0, s * 0.3, 0, Math.PI * 2)
        ctx.fillStyle = '#fff0f7'
        ctx.fill()
        ctx.stroke()
      }
      ctx.restore()
    },

    /* ---------- 交互 ---------- */
    onCanvasClick (e, side) {
      if (this.won) return
      const canvas = e.target
      const rect = canvas.getBoundingClientRect()
      const x = (e.clientX - rect.left) * (W / rect.width)
      const y = (e.clientY - rect.top) * (H / rect.height)
      // 命中未找出的差异
      for (const idx of this.diffs) {
        if (this.found.includes(idx)) continue
        const el = this.elements[idx]
        const dx = x - el.x
        const dy = y - el.y
        const radius = Math.max(26, el.size * 0.8)
        if (dx * dx + dy * dy <= radius * radius) {
          this.found.push(idx)
          this.drawAll()
          if (this.found.length >= DIFF_TOTAL) {
            this.winLevel()
          }
          return
        }
      }
      // 点错
      this.misses++
      const marker = { x, y, id: Date.now() + Math.random() }
      this.missMarkers.push(marker)
      this.drawAll()
      const self = this
      setTimeout(() => {
        self.missMarkers = self.missMarkers.filter(m => m.id !== marker.id)
        self.drawAll()
      }, 600)
    },
    drawMissMarkers (ctx) {
      for (const m of this.missMarkers) {
        ctx.save()
        ctx.strokeStyle = '#9a7b8a'
        ctx.lineWidth = 3
        ctx.beginPath()
        ctx.moveTo(m.x - 12, m.y - 12)
        ctx.lineTo(m.x + 12, m.y + 12)
        ctx.moveTo(m.x + 12, m.y - 12)
        ctx.lineTo(m.x - 12, m.y + 12)
        ctx.stroke()
        ctx.restore()
      }
    },

    /* ---------- 提示 ---------- */
    useHint () {
      if (this.hints <= 0 || this.won) return
      const target = this.diffs.find(i => !this.found.includes(i))
      if (target === undefined) return
      this.found.push(target)
      this.hints--
      try { localStorage.setItem(HINT_KEY, String(this.hints)) } catch (e) {}
      this.drawAll()
      if (this.found.length >= DIFF_TOTAL) {
        this.winLevel()
      }
    },

    /* ---------- 过关 ---------- */
    winLevel () {
      this.won = true
      this.stopTimer()
      if (this.level >= this.unlockedLevel && this.level < MAX_LEVEL) {
        this.unlockedLevel = Math.min(MAX_LEVEL, this.level + 1)
        try { localStorage.setItem(LV_KEY, String(this.unlockedLevel)) } catch (e) {}
      }
    },
    stopTimer () {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    }
  }
}
</script>

<style scoped>
.fd {
  font-family: "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", sans-serif;
  color: #6b4a5a;
  max-width: 900px;
  margin: 0 auto;
}

/* ---------- 顶栏 ---------- */
.fd-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 10px 16px;
  margin-bottom: 12px;
  border-radius: 14px;
  background: #fff7fb;
  border: 1px solid #ffddea;
}
.fd-info {
  font-size: 14px;
  font-weight: 600;
  color: #b06a8a;
}
.fd-info.warn { color: #ff5f6d; }
.fd-hint-btn {
  margin-left: auto;
  border: 2px solid #ffd3e4;
  border-radius: 999px;
  padding: 7px 16px;
  background: #fff;
  color: #e25b96;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
}
.fd-hint-btn:hover:not(:disabled) {
  border-color: #ff6fa5;
  background: #ffeef6;
}
.fd-hint-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.fd-refresh {
  border: 2px solid #ffd3e4;
  border-radius: 999px;
  width: 34px;
  height: 34px;
  background: #fff;
  color: #e25b96;
  font-size: 15px;
  cursor: pointer;
  transition: all 0.2s ease;
}
.fd-refresh:hover { border-color: #ff6fa5; background: #ffeef6; }

/* ---------- 选关 ---------- */
.fd-levels {
  display: none;
  grid-template-columns: repeat(10, 1fr);
  gap: 8px;
  padding: 12px;
  margin-bottom: 10px;
  border-radius: 14px;
  background: #fff7fb;
  border: 1px solid #ffddea;
}
.fd-levels.open { display: grid; }
.fd-level {
  aspect-ratio: 1;
  border: 2px solid #ffd3e4;
  border-radius: 10px;
  background: #fff;
  color: #e25b96;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
}
.fd-level:hover:not(.locked) { border-color: #ff6fa5; background: #ffeef6; }
.fd-level.active {
  background: linear-gradient(135deg, #ff8fc7, #ff6fa5);
  border-color: #ff6fa5;
  color: #fff;
}
.fd-level.locked {
  opacity: 0.4;
  cursor: not-allowed;
  color: #c9b3c0;
}
.fd-menu-toggle {
  display: block;
  margin: 0 auto 14px;
  border: none;
  background: transparent;
  color: #c490a8;
  font-size: 13px;
  cursor: pointer;
}
.fd-menu-toggle:hover { color: #e25b96; }

/* ---------- 画布 ---------- */
.fd-canvas-wrap {
  position: relative;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 10px;
  align-items: center;
}
.fd-canvas {
  width: 100%;
  height: auto;
  border-radius: 14px;
  border: 2px solid #ffd3e4;
  background: #fff;
  box-shadow: 0 8px 22px rgba(255, 158, 198, 0.25);
  cursor: crosshair;
  touch-action: manipulation;
}
.fd-vs {
  writing-mode: vertical-lr;
  font-size: 14px;
  font-weight: 800;
  color: #ff6fa5;
  letter-spacing: 4px;
}

/* ---------- 过关遮罩 ---------- */
.fd-overlay {
  position: fixed;
  inset: 0;
  z-index: 60;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: rgba(255, 240, 247, 0.82);
  backdrop-filter: blur(3px);
}
.fd-overlay-emoji { font-size: 56px; animation: fd-bounce 0.5s ease; }
@keyframes fd-bounce {
  0% { transform: scale(0.4); opacity: 0; }
  60% { transform: scale(1.15); }
  100% { transform: scale(1); opacity: 1; }
}
.fd-overlay-title {
  font-size: 24px;
  font-weight: 800;
  color: #e25b96;
}
.fd-overlay-info {
  font-size: 14px;
  color: #b06a8a;
}
.fd-overlay-btns {
  display: flex;
  gap: 12px;
  margin-top: 8px;
}
.fd-btn {
  border: none;
  border-radius: 999px;
  padding: 10px 26px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
}
.fd-btn-pink {
  background: linear-gradient(135deg, #ff8fc7, #ff6fa5);
  color: #fff;
  box-shadow: 0 5px 14px rgba(255, 111, 165, 0.35);
}
.fd-btn-pink:hover { transform: translateY(-2px); }
.fd-btn-line {
  background: #fff;
  border: 2px solid #ffd3e4;
  color: #b06a8a;
}
.fd-btn-line:hover { border-color: #ff6fa5; color: #e25b96; }

@media (max-width: 700px) {
  .fd-canvas-wrap { grid-template-columns: 1fr; }
  .fd-vs { writing-mode: horizontal-tb; text-align: center; padding: 4px 0; }
  .fd-levels { grid-template-columns: repeat(6, 1fr); }
}
</style>
